// fingerprint.ts — device lens-profile identity (ARCHITECTURE.md §14). Builds
// a canonical JSON of the stable facts about this phone + camera (UA platform
// and model, screen geometry, the camera track's label/facing/capability
// ranges), hashes it with SHA-256, and derives the profile key
// `fingerprint:WxH:zoom` (a different stream resolution or crop is a
// different intrinsic model) plus a human label like
// "Pixel 8 · rear · 1920×1080". deviceId is deliberately not used: it is
// per-origin and resettable, and nothing versioned (browser or OS version,
// UA-CH platformVersion) is hashed, so updates keep the same key and the
// profile keeps being reused. Every browser API is optional — a missing one
// just drops its field, so the key is always computable.

import { sha256Hex } from './sha256';

export interface DeviceFingerprint {
  key: string; // `${fingerprint}:${W}x${H}:${zoom}`
  fingerprint: string; // sha256 hex of `canonical`
  label: string;
  width: number;
  height: number;
  zoom: number;
  canonical: string; // the hashed JSON (debug only — never logged at info)
}

// Narrow views of the APIs we read, so missing ones type-check as undefined.
interface UADataLike {
  platform?: string;
  mobile?: boolean;
  getHighEntropyValues?: (hints: string[]) => Promise<Record<string, unknown>>;
}
export interface NavigatorLike {
  userAgent?: string;
  userAgentData?: UADataLike;
}
export interface ScreenLike {
  width?: number;
  height?: number;
}
export interface TrackLike {
  label?: string;
  // `object`, not a record: MediaTrackSettings has no index signature.
  getSettings?: () => object;
  getCapabilities?: () => object;
}
export interface FingerprintEnv {
  nav?: NavigatorLike;
  screen?: ScreenLike;
  dpr?: number;
  track?: TrackLike | null;
  // The frame size the tracker actually sees (video.videoWidth×Height);
  // preferred over getSettings() because it carries the real orientation.
  frameWidth?: number;
  frameHeight?: number;
}

type Json = string | number | boolean | null | Json[] | { [k: string]: Json };

// canonicalJSON serializes with sorted keys, no undefined members and
// numbers rounded to 4 decimals, so equal facts always hash equal.
export function canonicalJSON(v: unknown): string {
  return JSON.stringify(canonicalize(v));
}

function canonicalize(v: unknown): Json | undefined {
  if (v === null) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? Math.round(v * 1e4) / 1e4 : null;
  if (typeof v === 'string' || typeof v === 'boolean') return v;
  if (Array.isArray(v)) return v.map((x) => canonicalize(x) ?? null);
  if (typeof v === 'object') {
    const out: { [k: string]: Json } = {};
    for (const k of Object.keys(v as object).sort()) {
      const c = canonicalize((v as Record<string, unknown>)[k]);
      if (c !== undefined) out[k] = c;
    }
    return out;
  }
  return undefined;
}

// normalizeUA blanks version numbers (browser/OS updates must not change the
// key) but keeps model names such as "Pixel 8" or "SM-S918B" intact.
export function normalizeUA(ua: string): string {
  return ua
    .replace(/\s*Build\/[^;)]*/g, '') // Android build id: changes every OS patch
    .replace(/\/[\d._]+/g, '/#')
    .replace(/\b(Android|OS|Version|Windows NT|Mac OS X|CrOS \S+|rv:)\s*[\d._]+/g, '$1 #')
    .replace(/\s+/g, ' ')
    .trim();
}

// modelFromUA pulls a display model out of a UA string (best effort).
export function modelFromUA(ua: string): string {
  const android = /Android [^;)]*;\s*(?:[a-z]{2}[-_][A-Za-z]{2};\s*)?([^;)]+?)(?:\s+Build\/[^;)]*)?\)/.exec(ua);
  if (android && android[1] && android[1] !== 'K') return android[1].trim();
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows PC';
  if (/Android/.test(ua)) return 'Android phone';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Unknown device';
}

function range(v: unknown): [number, number] | undefined {
  if (!v || typeof v !== 'object') return undefined;
  const r = v as { min?: unknown; max?: unknown };
  return typeof r.min === 'number' && typeof r.max === 'number' ? [r.min, r.max] : undefined;
}

function num(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v ? v : undefined;
}

function safeCall<T>(fn: (() => T) | undefined, what: string): T | undefined {
  if (!fn) return undefined;
  try {
    return fn();
  } catch (err) {
    console.debug(`[openscan/fingerprint] ${what} threw`, err);
    return undefined;
  }
}

// facingName maps facingMode to the label word.
export function facingName(facing: string | undefined): string {
  switch (facing) {
    case 'environment':
      return 'rear';
    case 'user':
      return 'front';
    case undefined:
    case '':
      return 'camera';
    default:
      return facing;
  }
}

export function fmtZoom(zoom: number): string {
  return String(Math.round(zoom * 100) / 100);
}

export function profileKey(fingerprint: string, width: number, height: number, zoom: number): string {
  return `${fingerprint}:${width}x${height}:${fmtZoom(zoom)}`;
}

export function deviceLabel(model: string, facing: string | undefined, width: number, height: number, zoom: number): string {
  const parts = [model, facingName(facing), `${width}×${height}`];
  if (Math.abs(zoom - 1) > 1e-3) parts.push(`${fmtZoom(zoom)}× zoom`);
  return parts.join(' · ');
}

// collectFingerprint gathers the facts, hashes them and builds key + label.
// It never throws: every unavailable API only removes its own field.
export async function collectFingerprint(env: FingerprintEnv = defaultEnv()): Promise<DeviceFingerprint> {
  const nav = env.nav ?? {};
  const ua = nav.userAgent ?? '';
  let uaFacts: Record<string, unknown> = { ua: normalizeUA(ua) };
  let model = modelFromUA(ua);
  const uad = nav.userAgentData;
  if (uad?.getHighEntropyValues) {
    try {
      // platformVersion is deliberately not requested: an OS update must not
      // change the key and orphan the phone's measured profile.
      const he = await uad.getHighEntropyValues(['model', 'platform']);
      uaFacts = { platform: str(he.platform) ?? str(uad.platform) ?? '', model: str(he.model) ?? '', mobile: uad.mobile };
      if (str(he.model)) model = String(he.model);
      else if (str(he.platform)) model = model === 'Unknown device' ? String(he.platform) : model;
    } catch (err) {
      console.debug('[openscan/fingerprint] getHighEntropyValues refused; using the UA string', err);
    }
  }

  // Screen orientation flips width/height on some browsers: store long×short.
  const sw = num(env.screen?.width) ?? 0, sh = num(env.screen?.height) ?? 0;
  const screenFacts = { long: Math.max(sw, sh), short: Math.min(sw, sh), dpr: num(env.dpr) ?? 1 };

  const track = env.track ?? null;
  const settings = (safeCall(track?.getSettings?.bind(track), 'getSettings') ?? {}) as Record<string, unknown>;
  const caps = (safeCall(track?.getCapabilities?.bind(track), 'getCapabilities') ?? {}) as Record<string, unknown>;
  const facing = str(settings.facingMode) ?? (Array.isArray(caps.facingMode) ? str(caps.facingMode[0]) : undefined);
  const capW = range(caps.width), capH = range(caps.height);
  const cameraFacts = {
    label: str(track?.label),
    facing,
    max_w: capW?.[1],
    max_h: capH?.[1],
    zoom: range(caps.zoom),
    focus: range(caps.focusDistance),
  };

  const width = Math.round(num(env.frameWidth) || num(settings.width) || 0);
  const height = Math.round(num(env.frameHeight) || num(settings.height) || 0);
  const zoom = num(settings.zoom) ?? 1;

  const canonical = canonicalJSON({ v: 1, ua: uaFacts, screen: screenFacts, camera: cameraFacts });
  const fingerprint = await sha256Hex(canonical);
  const key = profileKey(fingerprint, width, height, zoom);
  const label = deviceLabel(model, facing, width, height, zoom);
  console.info(`[openscan/fingerprint] fp=${fingerprint.slice(0, 12)}… ${width}x${height} zoom=${fmtZoom(zoom)} label="${label}"`);
  console.debug('[openscan/fingerprint] canonical facts', canonical);
  return { key, fingerprint, label, width, height, zoom, canonical };
}

function defaultEnv(): FingerprintEnv {
  const g = globalThis as { navigator?: NavigatorLike; screen?: ScreenLike; devicePixelRatio?: number };
  return { nav: g.navigator, screen: g.screen, dpr: g.devicePixelRatio };
}

// browserEnv builds the env for a live camera track and its video element.
export function browserEnv(track: TrackLike | null, frameWidth: number, frameHeight: number): FingerprintEnv {
  return { ...defaultEnv(), track, frameWidth, frameHeight };
}
